import React, { useState, useEffect } from 'react'
import { ClockIcon } from '@heroicons/react/24/outline'

interface CountdownTimerProps {
  targetDate: string | Date
  className?: string
  size?: 'small' | 'medium' | 'large'
  variant?: 'default' | 'urgent' | 'expired'
  onComplete?: () => void
}

export function CountdownTimer({
  targetDate,
  className = '',
  size = 'medium',
  variant = 'default',
  onComplete
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0
  })
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    const target = new Date(targetDate).getTime()

    const calculateTimeLeft = () => {
      const now = new Date().getTime()
      const difference = target - now

      if (difference <= 0) {
        setIsExpired(true)
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 })
        onComplete?.()
        return
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
        totalSeconds: Math.floor(difference / 1000)
      })
    }

    calculateTimeLeft()
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [targetDate, onComplete])

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'text-sm',
          time: 'text-lg font-semibold',
          label: 'text-xs'
        }
      case 'large':
        return {
          container: 'text-lg',
          time: 'text-3xl font-bold',
          label: 'text-sm'
        }
      default: // medium
        return {
          container: 'text-base',
          time: 'text-xl font-bold',
          label: 'text-xs'
        }
    }
  }

  const getVariantClasses = () => {
    if (isExpired) {
      return 'text-red-600 bg-red-50 border-red-200'
    }

    const hoursLeft = timeLeft.days * 24 + timeLeft.hours

    switch (variant) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        if (hoursLeft <= 2) {
          return 'text-red-600 bg-red-50 border-red-200'
        } else if (hoursLeft <= 6) {
          return 'text-amber-600 bg-amber-50 border-amber-200'
        } else {
          return 'text-green-600 bg-green-50 border-green-200'
        }
    }
  }

  const formatTimeUnit = (value: number) => {
    return value.toString().padStart(2, '0')
  }

  const sizeClasses = getSizeClasses()
  const variantClasses = getVariantClasses()

  if (isExpired) {
    return (
      <div className={`inline-flex items-center px-3 py-2 rounded-md border ${variantClasses} ${sizeClasses.container} ${className}`}>
        <ClockIcon className="w-4 h-4 mr-2" />
        <span className="font-medium">Payment Expired</span>
      </div>
    )
  }

  return (
    <div className={`inline-flex items-center px-3 py-2 rounded-md border ${variantClasses} ${sizeClasses.container} ${className}`}>
      <ClockIcon className="w-4 h-4 mr-2" />
      <div className="flex items-center space-x-1">
        {timeLeft.days > 0 && (
          <>
            <span className={sizeClasses.time}>{formatTimeUnit(timeLeft.days)}</span>
            <span className={sizeClasses.label}>d</span>
            <span className="mx-1">:</span>
          </>
        )}
        <span className={sizeClasses.time}>{formatTimeUnit(timeLeft.hours)}</span>
        <span className={sizeClasses.label}>h</span>
        <span className="mx-1">:</span>
        <span className={sizeClasses.time}>{formatTimeUnit(timeLeft.minutes)}</span>
        <span className={sizeClasses.label}>m</span>
        <span className="mx-1">:</span>
        <span className={sizeClasses.time}>{formatTimeUnit(timeLeft.seconds)}</span>
        <span className={sizeClasses.label}>s</span>
      </div>
    </div>
  )
}

export function PaymentDeadlineCountdown({
  dueDate,
  className = ''
}: {
  dueDate: string | Date
  className?: string
}) {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className="text-sm text-neutral-600">Payment Due:</span>
      <CountdownTimer
        targetDate={dueDate}
        size="small"
        className="font-medium"
      />
    </div>
  )
}